<script setup lang="ts">
import { computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n } from '@/i18n'
import AdjustmentControls from './AdjustmentControls.vue'
import FillControls from './FillControls.vue'
import TextControls from './TextControls.vue'
import SmartControls from '@/components/panels/SmartControls.vue'
import ShapeControls from './ShapeControls.vue'

const docStore = useDocumentStore()
const { t } = useI18n()
const layer = computed(() => docStore.activeLayer)
const rect = computed(() => (layer.value ? docStore.layerRect(layer.value.id) : null))
function setName(e: Event) {
  const v = (e.target as HTMLInputElement).value.trim()
  if (layer.value && v && v !== layer.value.name) docStore.setLayerProps(layer.value.id, { name: v }, 'history.renameLayer')
}
function setFill(e: Event) {
  if (layer.value) docStore.setLayerProps(layer.value.id, { fillOpacity: Number((e.target as HTMLInputElement).value) / 100 })
}
function setOffset(axis: 'x' | 'y', e: Event) {
  const l = layer.value
  if (!l || l.kind !== 'raster') return
  const v = Number((e.target as HTMLInputElement).value)
  if (!Number.isFinite(v)) return
  docStore.setLayerOffset(l.id, { ...l.offset, [axis]: v })
}
</script>

<template>
  <div class="panel">
    <div class="panel-title">{{ t('panel.properties') }}</div>
    <div v-if="layer" class="body">
      <label class="field"><span class="muted">{{ t('layer.name') }}</span><input :value="layer.name" @change="setName" /></label>
      <label class="field"><span class="muted">{{ t('layer.fill') }}</span>
        <span class="row"><input type="range" min="0" max="100" :value="Math.round(layer.fillOpacity * 100)" @change="setFill" /><span class="mono">{{ Math.round(layer.fillOpacity * 100) }}%</span></span>
      </label>
      <AdjustmentControls v-if="layer.kind === 'adjustment'" :layer="layer" />
      <FillControls v-else-if="layer.kind === 'fill'" :layer="layer" />
      <TextControls v-else-if="layer.kind === 'text'" :layer="layer" />
      <ShapeControls v-else-if="layer.kind === 'shape'" :layer="layer" />
      <SmartControls v-else-if="layer.kind === 'smart'" :layer="layer" />
      <template v-if="layer.kind === 'raster'">
        <label class="field"><span class="muted">X</span><input type="number" :value="layer.offset.x" @change="setOffset('x', $event)" /></label>
        <label class="field"><span class="muted">Y</span><input type="number" :value="layer.offset.y" @change="setOffset('y', $event)" /></label>
        <div v-if="rect" class="field"><span class="muted">W × H</span><span class="mono">{{ rect.w }} × {{ rect.h }}</span></div>
      </template>
    </div>
    <div v-else class="body muted empty">—</div>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
}
.body {
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field {
  display: grid;
  grid-template-columns: 56px 1fr;
  align-items: center;
  gap: 8px;
}
.field input {
  min-width: 0;
  width: 100%;
}
.field input[type='number'] {
  width: 80px;
}
.field input[type='range'] {
  width: 100px;
}
.empty {
  text-align: center;
}
</style>
